import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Plus, Search } from 'lucide-react';

interface Props {
  count: number;
  total: number;
  onContinue: () => void;
  onAddAnother: () => void;
  showAddAnother: boolean;
  verifyMode: boolean;
}

const formatCOP = (amount: number) =>
  new Intl.NumberFormat('es-CO').format(amount);

export default function StickyBar({ count, total, onContinue, onAddAnother, showAddAnother, verifyMode }: Props) {
  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className="fixed bottom-0 inset-x-0 z-50"
      style={{ backgroundColor: 'var(--sticky-bg)', backdropFilter: 'blur(20px)', borderTop: '1px solid var(--border-color)', boxShadow: '0 -4px 30px rgba(0,0,0,0.08)' }}
    >
      <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-bold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'var(--text-primary)' }}>
            {count} consulta{count > 1 ? 's' : ''}
          </p>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Total:{' '}
            <span className="font-bold text-[#2563EB]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              ${formatCOP(total)} COP
            </span>
          </p>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <AnimatePresence>
            {showAddAnother && (
              <motion.button
                key="add-another"
                data-testid="add-another-sticky-btn"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={onAddAnother}
                className="flex items-center gap-1.5 px-4 py-3 rounded-2xl font-semibold border-2 transition-all"
                style={{ borderColor: '#2563EB', color: '#2563EB', backgroundColor: 'transparent', fontSize: '14px' }}
              >
                <Plus size={15} />
                <span className="hidden sm:inline">Agregar otra</span>
                <span className="sm:hidden">+ Otra</span>
              </motion.button>
            )}
          </AnimatePresence>

          <motion.button
            data-testid="sticky-continue-btn"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={onContinue}
            className="flex items-center gap-2 px-6 py-3 rounded-2xl font-bold text-white transition-all"
            style={{ backgroundColor: '#2563EB', boxShadow: '0 4px 20px rgba(37,99,235,0.3)' }}
          >
            <AnimatePresence mode="wait" initial={false}>
              {verifyMode ? (
                <motion.span
                  key="verify"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.18 }}
                  className="flex items-center gap-2"
                >
                  <Search size={17} />
                  Verificar y agregar
                </motion.span>
              ) : (
                <motion.span
                  key="continue"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.18 }}
                  className="flex items-center gap-2"
                >
                  Continuar
                  <ArrowRight size={18} />
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
}
